import React, { useContext, useEffect, useState } from 'react';
import NotificationRecipientsSelectorDialog from '../NotificationThreadConfig/NotificationRecipientsSelectorDialog';
import { getSelectedRecipientData, deactivateNotificationThread } from '../../services/notification.services';
import { CHANNELS } from '../../constant-data';
import style from './NotificationListItem.module.css';
import fontSize from '../Style/FontSize.module.css';
import common from '../Style/common.module.css';
import font from '../Style/FontFamily.module.css';
import { toast } from 'react-toastify';
import ConfirmAlertModal from '../UI/ConfirmAlertModal/ConfirmAlertModal';
import AuthContext from "../../store/auth-context";
import Tooltip from 'react-bootstrap/Tooltip';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';

/**
 * Utility functions for date/time formatting and message parsing
 */

// Calculate time elapsed since a given date
const formatTimeAgo = (date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / 86400000);
    const diffHours = Math.floor((diffMs % 86400000) / 3600000);
    const diffMinutes = Math.round(((diffMs % 86400000) % 3600000) / 60000);

    if (diffDays > 0) return `${diffDays} Days`;
    if (diffHours > 0) return `${diffHours} hours`;
    return `${diffMinutes} minutes`;
};

// Format date and time for display
const formatDateTime = (date) => {
    const day = date.toLocaleString('en-US', { day: '2-digit' });
    const month = date.toLocaleString('en-US', { month: 'numeric' });
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return {
        dateStr: `${day}/${month}/${year}`,
        timeStr: `${hours}:${minutes}`
    };
};

// Parse private message format "username@@@subject-#DRFT#"
const parsePrivateMessage = (title) => {
    const parts = title.split('@@@');
    return {
        username: parts[0] || '',
        subject: (parts.length > 1 ? parts[1] : title).replace('-#DRFT#', '')
    };
};

const renderTooltip = (props) => (
    <Tooltip id="button-tooltip" {...props}>
        Statistics
    </Tooltip>
);

/**
 * NotificationThreadListItem Component
 * 
 * Renders a single notification thread item in the list with:
 * - Author information and avatar
 * - Message title and preview
 * - Channel indicators
 * - Action buttons (statistics, deactivate)
 * - Special handling for private messages (channel "5")
 * 
 * @param {Object} props - Component props
 * @param {string} props.contactGroup - Contact group ID
 * @param {string} props.selectedContactGroup - Currently selected contact group ID
 * @param {string} props.contactGroupStatus - Status (ACTIVE/INACTIVE)
 * @param {string} props.channels - Comma-separated channel IDs
 * @param {string} props.messageTitle - Message title
 * @param {string} props.messageBody - Message content
 * @param {string} props.authorName - Author name
 * @param {string} props.authorImageUrl - Author avatar URL
 * @param {string} props.lastModified - Last modification date
 * @param {Function} props.onClick - Click handler
 * @param {Function} props.onSelectDeactivateNotification - Deactivation handler
 */
const NotificationThreadListItem = (props) => {
    const [privateMessageUser, setPrivateMessageUser] = useState('');
    const authCtx = useContext(AuthContext);
    
    const { 
        notificationThreadStatus,
        selectedContactGroup,
        onSelectDeactivateNotification,
        contactGroup,
        channels,
        messageTitle,
        authorImageUrl
    } = props;

    const isPrivateMessage = channels.includes("5");
    
    useEffect(() => {
        if (isPrivateMessage) {
            parsePrivateMessage(messageTitle);

            // Handle previewedRecipientData - might be string, array, or null
            let recipient: any = null;
            if (props.previewedRecipientData) {
                try {
                    // Check if it's already an array/object
                    if (typeof props.previewedRecipientData === 'object') {
                        recipient = Array.isArray(props.previewedRecipientData) 
                            ? props.previewedRecipientData[0] 
                            : props.previewedRecipientData;
                    } else if (typeof props.previewedRecipientData === 'string' && props.previewedRecipientData.trim()) {
                        // It's a non-empty string, try to parse it
                        const parsed = JSON.parse(props.previewedRecipientData);
                        recipient = Array.isArray(parsed) ? parsed[0] : parsed;
                    }
                } catch (error) {
                    console.error('Error parsing previewedRecipientData:', error);
                    recipient = null;
                }
            }
            
            console.log('recipient', recipient);
            setPrivateMessageUser(recipient?.name || '');

        }
    }, [isPrivateMessage, messageTitle, props.previewedRecipientData]);


    const [isRecipientModalOpen, setIsRecipientModalOpen] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [selectedCategoryTags, setSelectedCategoryTags] = useState([]);
    const [selectedEmailOnlySubscribers, setselectedEmailOnlySubscribers] = useState([]);
    const [isAllSchoolLinkAppUsersSelected, setIsAllSchoolLinkAppUsersSelected] = useState(false);
    const [isAllEmailOnlySubscribersSelected, setIsAllEmailOnlySubscribersSelected] = useState(false);
    const [previewedRecipientData, setPreviewedRecipientData] = useState([]);
    const [showAlert, setShowAlert] = useState(false);
    const [isSending, setIsSending] = useState(false);


    // Date and time formatting
    const lastModifiedDate = new Date(props.lastModified);
    const timeAgo = formatTimeAgo(lastModifiedDate);
    const { dateStr, timeStr } = formatDateTime(lastModifiedDate);

    // Style classes
    const isSelected = contactGroup === selectedContactGroup;
    const activeStyle = isSelected ? style.active : '';
    const filterActive = isSelected ? 'filterAction' : '';
    const isInactive = notificationThreadStatus === 'INACTIVE';

    const recipientModalHendler = () => {
        if (!isRecipientModalOpen) {
            getSelectedRecipientData(contactGroup, authCtx.token)
                .then((response) => {

                    const sliceCatTags = [];
                    const categoriesTags = response.data.usergroupcategory;

                    if (categoriesTags.length > 0) {

                        categoriesTags.forEach(catTags => {

                            const temp = {
                                [catTags.categoryID]: catTags.usergrouptag.map(t => ({ label: t.name, value: t.tagId }))
                            }
                            sliceCatTags.push(temp);
                        });
                    }
                    const selectedusersStr = response.data.selectedUsers;
                    const updateSelectedUsers = !selectedusersStr ? [] : selectedusersStr.split(",");
                    setSelectedUsers(updateSelectedUsers);
                    setSelectedCategoryTags(sliceCatTags);
                    setselectedEmailOnlySubscribers(response.data.emailOnlySubscribers);

                }).catch((error) => {
                    console.log(error);
                })
        }
        setIsRecipientModalOpen((prevState) => !prevState);
    }

    /** For deactivate  notificationThread (start here. . .)   **/

    const deactiveNotificationHandler = (e) => {
        e.stopPropagation();
        setShowAlert(true);
    }
    const onConfirmHandler = () => {
        setIsSending(true);
        deactivateNotificationThread(contactGroup, { "action": "INACTIVE" }, authCtx.token)
            .then((res) => {
                if (res.status) {
                    setIsSending(false);
                    setShowAlert(false);
                    onSelectDeactivateNotification(contactGroup);
                    toast.success("Notification thread deactivated successfully ", {
                        position: toast.POSITION.BOTTOM_RIGHT,
                        type: 'success'
                    });
                } else {
                    setIsSending(false);
                }
            })
            .catch((err) => {
                setIsSending(false);
                console.log(err);
            });
    }
    const onCancelHandler = () => {
        setShowAlert(false);
    }
    /*** End Here ***/

    // Message processing
    const channelsArr = channels ? channels.split(',') : [];
    const isDraft = props.messageStatus === 'DRAFT';
    const messagePreview = isDraft 
        ? ` ${props.messageBody.length > 110 ? props.messageBody.substring(0, 107) + "..." : props.messageBody}`
        : (props.messageBody.length > 110 ? props.messageBody.substring(0, 107) + "..." : props.messageBody);
    
    const displayTitle = isPrivateMessage 
        ? messageTitle.replace("-#DRFT#", "")
        : messageTitle.replace("-#DRFT#", "");
        
    //const displayMessage = isPrivateMessage ? subjectLine : messagePreview;
    const displayMessage = messagePreview;

   // console.log("message", props);

    return (<>
        {isRecipientModalOpen && <NotificationRecipientsSelectorDialog
            modalStatus={isRecipientModalOpen}
            onAction={recipientModalHendler}
            selectedUsers={selectedUsers}
            setSelectedUsers={setSelectedUsers}
            selectedCategoriesTags={selectedCategoryTags}
            setSelectedCategoryTags={setSelectedCategoryTags}
            selectedEmailOnlySubscribers={selectedEmailOnlySubscribers}
            setselectedEmailOnlySubscribers={setselectedEmailOnlySubscribers}
            isAllSchoolLinkAppUsersSelected={isAllSchoolLinkAppUsersSelected}
            setIsAllSchoolLinkAppUsersSelected={setIsAllSchoolLinkAppUsersSelected}
            isAllEmailOnlySubscribersSelected={isAllEmailOnlySubscribersSelected}
            setIsAllEmailOnlySubscribersSelected={setIsAllEmailOnlySubscribersSelected}
            isRecipientUpdate={props.contactGroup}
            channels={props.channels}
            saveNotificationThreadConfig={() => {}}
            setSelectUsersForGroup={() => {}}
            setRecipientsCount={() => {}}
            setPreviewedRecipientData={setPreviewedRecipientData}
            previewedRecipientData={previewedRecipientData}
        />}

        {showAlert && <ConfirmAlertModal
            show={showAlert}
            onHide={onCancelHandler}
            isSending={isSending}
            onConfirm={onConfirmHandler}
        />}

        <div 
            key={`${Math.random()}-${props.messageId}`} 
            style={{ position: "relative" }}
            className={`${style.messageNoti} ${common.messageEffect} ${activeStyle} ${filterActive} ${isInactive ? style.inactiveMessageThread : ''}`}
            onClick={!isSelected ? props.onClick : undefined}
        >
            {isInactive && <div className={common.deactiveitem}></div>}
            
            <div className={style.List}>
                <div className={style.messageList}>
                    <div className={style.userimage}>
                        <img src={authorImageUrl || 'https://static.myschoollink.app/common/profile-icon-login-head-icon-vector.png'} alt={props.authorName} />
                    </div>
                    
                    <div className={style.Messagearea}>
                        <div className={style.MessageSub}>
                            <p className={`${common.messegeTitle} ${fontSize.smSize} ${font.HelveticaNeue700}`} title={selectedContactGroup}>
                                {!isPrivateMessage ? (
                                    <span className={`${common.msgDes} ${style.truncateTitleOfNotification}`}>
                                        {displayTitle}
                                    </span>
                                ) : (
                                    <span className={common.msgDes}>
                                        <span style={{color:'#888888'}}>Direct Messages to</span> {privateMessageUser}
                                    </span>
                                )}
                                
                                <span className={`${style.rightFilter} ${common.hoverEffect} ${style.activeEffect}`} style={{padding:"0"}}>
                                    {!isPrivateMessage && (
                                        <button>
                                            <OverlayTrigger
                                                placement="top"
                                                delay={{ show: 50, hide: 400 }}
                                                overlay={renderTooltip}
                                            >
                                                <span className={style.statesSvgIcon}></span>
                                            </OverlayTrigger>
                                            <span className={common.hideBtnText}>Statistics</span>
                                        </button>
                                    )}

                                    <button onClick={deactiveNotificationHandler}>
                                        {isInactive ? (
                                            <span className={style.lockedSvgIcon}></span>
                                        ) : (
                                            <span className={style.unlockedSvgIcon}></span>
                                        )}
                                        <span className={common.hideBtnText}>DeactiveNotification</span>
                                    </button>
                                </span>
                            </p>
                            
                            <p className={`${common.autherName} ${fontSize.font13} ${font.HelveticaNeue400}`}>
                                <span className={common.dott}>
                                    By {props.authorName} <span>{timeAgo}</span>{" "}
                                </span>
                                {isPrivateMessage ? (
                                    <span className={`${common.orgTag} ${fontSize.lowXsSize}`} style={{background:'#0b5ed7'}}>
                                        Private Notification
                                    </span>
                                ) : (
                                    <span className={`${common.orgTag} ${fontSize.lowXsSize}`}>
                                        Group Notification
                                    </span>
                                )}
                            </p>
                            
                            <p className={`${common.threadLatestMessage} ${fontSize.font13} ${font.HelveticaNeue400}`}>
                                {props.messageStatus === 'DRAFT' && <span style={{color:'#e72253ff'}}>DRAFT: </span>} {displayMessage}
                            </p>
                            
                            <p className={`${common.messageDetail} ${fontSize.font13} ${font.HelveticaNeue400} ${style.activeEffect}`}>
                                {channelsArr.map(channelId => (
                                    <SVGIcon 
                                        key={`${channelId}-${props.messageId}`} 
                                        svgIcondata={CHANNELS.find(i => i.id === channelId)?.svgIcon || ''} 
                                    />
                                ))}
                                <span className={style.messageDate}>
                                    {props.messageStatus === 'DRAFT' ? 'Edited' : 'Sent'} {timeStr} - {dateStr}
                                </span>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </>)
}

const SVGIcon = ({ svgIcondata }) => (
    <span className="iconclass" dangerouslySetInnerHTML={{ __html: svgIcondata }}></span>
);

export default NotificationThreadListItem;
